import * as Bixby from './../../core';
import { AppState } from './..';
import { TokenService, AnalyticsService } from './../../services';

import {
  TokenResult,
  LOGIN_INPROGRESS,
  LOGIN_SUCCESS,
  LOGIN_FAILURE,
  LOGOUT_SUCCESS,
  LOGIN_CLEAR_PRELOADED_DATA,
  FEATURE_FLAGS_SUCCESS,
  LOGIN_MODAL_OPEN,
  LOGIN_MODAL_CLOSE,
} from './types';
import { getProfile } from '$/store/profile/actions';
import { ThunkAction } from 'redux-thunk';
import { ConnectService } from '$/services/ConnectService';
import { getAllStaticData, getAllAdminStaticData } from '$/store/staticData';
import {
  getIsCurrentUserAdminState,
  getIsCurrentUserSuperAdminState,
} from '$/store/profile/selectors';
import { FeatureFlagService } from '$/services/FeatureFlagService';

// action creators
export const loginInProgress = () => {
  return {
    type: LOGIN_INPROGRESS,
  };
};

export const loginSuccess = (tokenResult: TokenResult) => {
  return {
    type: LOGIN_SUCCESS,
    payload: tokenResult,
  };
};

export const loginClearPreloadedData = () => {
  return {
    type: LOGIN_CLEAR_PRELOADED_DATA,
  };
};

export const loginFailure = (error: Bixby.Models.BixbyError) => {
  return {
    type: LOGIN_FAILURE,
    payload: error,
    error: true,
  };
};

export const logoutSuccess = (error?: Bixby.Models.BixbyError) => {
  return {
    type: LOGOUT_SUCCESS,
    payload: error,
  };
};

export const featureFlagsSuccess = (payload: string[]) => {
  return {
    type: FEATURE_FLAGS_SUCCESS,
    payload,
  };
};

export const loginModalOpen = () => {
  return {
    type: LOGIN_MODAL_OPEN,
  };
};

export const loginModalClose = () => {
  return {
    type: LOGIN_MODAL_CLOSE,
  };
};

// thunk actions
const handleLoginError =
  (error: Bixby.Models.BixbyError): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> =>
  async dispatch => {
    if (Bixby.Utils.errorHasStatus(401, error)) {
      dispatch(loginFailure(new Error('Invalid username or password')));
      return;
    } else if (Bixby.Utils.errorHasStatus(400, error)) {
      const message = (error as any).response.data.errorDescription;
      dispatch(loginFailure(new Error(message)));
    } else if (Bixby.Utils.errorHasStatus(403, error)) {
      const errorCode: string = (error as any).response.data.error as string;
      let message = 'Unauthorized access.';
      if (errorCode === '403.6') {
        message = 'Invalid IP address';
      }
      dispatch(loginFailure(new Error(message)));
    } else {
      dispatch(loginFailure(new Error('An error occurred. Please try again later.')));
    }
  };

export const login = (
  loginRequest: Bixby.Models.LoginRequest
): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> => {
  return async dispatch => {
    dispatch(loginInProgress());
    try {
      await ConnectService.login(loginRequest);
      const token: string = (await TokenService.getAccessToken()) || '';
      dispatch(loginClearPreloadedData());
      dispatch(loginSuccess({ token } as TokenResult));
      AnalyticsService.login(loginRequest.email);

      await dispatch(preloadData());

      try {
        const featureFlags: string[] = await FeatureFlagService.getFeatureFlags();
        dispatch(featureFlagsSuccess(featureFlags));
      } catch (e: any) {
        AnalyticsService.exception(e);
        dispatch(featureFlagsSuccess([]));
      }
    } catch (error: any) {
      dispatch(handleLoginError(error));
    }
  };
};

export const loginExtend = (
  loginRequest: Bixby.Models.LoginRequest
): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> => {
  return async dispatch => {
    dispatch(loginInProgress());
    try {
      await ConnectService.login(loginRequest);
      const token: string = (await TokenService.getAccessToken()) || '';
      await dispatch(loginSuccess({ token } as TokenResult));
      AnalyticsService.event('login-extend', loginRequest.email);
    } catch (error: any) {
      await dispatch(handleLoginError(error));
      throw error;
    }
  };
};

export const preloadData =
  (): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> => async (dispatch, getState) => {
    const results = [dispatch(getProfile()).catch(i => i), dispatch(getAllStaticData()).catch(i => i)];
    const { isAdmin } = getIsCurrentUserAdminState(getState());
    const { isSuperAdmin } = getIsCurrentUserSuperAdminState(getState());
    
    if (isAdmin || isSuperAdmin) {
      results.push(dispatch(getAllAdminStaticData()).catch(i => i));
    }
    await Promise.all(results);
  };

export const resumeFromCache =
  (): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> => async dispatch => {
    if (TokenService.isAuthenticated()) {
      const access_token = TokenService.getAccessToken();
      const resumed: boolean = TokenService.resumeFromCache();

      if (resumed) {
        dispatch(loginClearPreloadedData());
        dispatch(loginSuccess({ token: access_token } as TokenResult));

        await dispatch(preloadData());

        try {
          const featureFlags: string[] = await FeatureFlagService.getFeatureFlags();
          dispatch(featureFlagsSuccess(featureFlags));
        } catch (e: any) {
          AnalyticsService.exception(e);
          dispatch(featureFlagsSuccess([]));
        }
      }
    }
  };

export const logout =
  (e?: Error): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> =>
  async dispatch => {
    await ConnectService.logout();
    AnalyticsService.logout();
    dispatch(logoutSuccess(e));
  };

export const openLoginModal =
  (): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> => async dispatch => {
    dispatch(loginModalOpen());
  };

export const closeLoginModal =
  (): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> => async dispatch => {
    dispatch(loginModalClose());
  };
